import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiSchool, mdiCalendarMultiple, mdiAccountMultiplePlusOutline, mdiCashMultiple, mdiHelpCircleOutline, mdiHelpCircle, mdiShareOutline, mdiAccountTie } from '@mdi/js';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import ClassesHelpModal from '../../components/ClassesHelpModal';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './ClassesPortal.css';
import '../../components/ClassesHelpModal.css';

/**
 * ManageClassesPortal - Setup and configuration pages for the class management system
 * Portal type: Sub-Portal Feature (.sub-portal-header-card)
 */
const ManageClassesPortal = () => {
    const navigate = useNavigate();
    const { user, checkPermission } = useAuth();
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    const isTeamMember = !checkPermission('viewDashboard');

    // Owner view: setup workflow with step badges
    const ownerNavCards = [
        {
            path: '/classes/terms',
            icon: mdiCalendarMultiple,
            title: 'Terms & Classes',
            subtitle: 'Create and manage terms, class schedules and enrolments',
            step: 1,
        },
        {
            path: '/classes/pricing',
            icon: mdiCashMultiple,
            title: 'Class Pricing',
            subtitle: 'Configure pricing packages and payment options for students',
            step: 2,
        },
        {
            path: '/classes/students/import',
            icon: mdiAccountMultiplePlusOutline,
            title: 'Student Upload',
            subtitle: 'Bulk import students from a CSV file',
            step: 3,
        },
        {
            path: '/classes/instructors',
            icon: mdiAccountTie,
            title: 'Instructors',
            subtitle: 'Assign instructors to terms and view session attendance reports',
        },
        {
            path: '/classes/promote',
            icon: mdiShareOutline,
            title: 'Promote',
            subtitle: 'Get shareable URLs for your school, class groups, and trial deals',
        },
    ];

    // Team member view: operations first
    const teamMemberNavCards = [
        {
            path: '/classes/students/import',
            icon: mdiAccountMultiplePlusOutline,
            title: 'Student Upload',
            subtitle: 'Bulk import students from a CSV file',
        },
        {
            path: '/classes/instructors',
            icon: mdiAccountTie,
            title: 'Instructors',
            subtitle: 'Assign instructors to terms and view session attendance reports',
        },
        {
            path: '/classes/terms',
            icon: mdiCalendarMultiple,
            title: 'Terms & Classes',
            subtitle: 'Create and manage terms, class schedules and enrolments',
        },
        {
            path: '/classes/pricing',
            icon: mdiCashMultiple,
            title: 'Class Pricing',
            subtitle: 'Configure pricing packages and payment options for students',
        },
        {
            path: '/classes/promote',
            icon: mdiShareOutline,
            title: 'Promote',
            subtitle: 'Get shareable URLs for your school, class groups, and trial deals',
        },
    ];

    const navCards = isTeamMember ? teamMemberNavCards : ownerNavCards;

    return (
        <>
        <div className="dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper" style={{ maxWidth: '900px' }}>
                    {/* Portal Header Card */}
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button
                                onClick={() => navigate('/classes')}
                                className="back-nav-btn">
                                ← Classes
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon path={mdiSchool} size={1.2} color="#451f78" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Manage Classes
                            </h1>
                        </div>
                        <div className="classes-subtitle-row">
                            <p className="sub-portal-header-subtitle">
                                Set up and configure terms, pricing, students, instructors and class settings.
                            </p>
                            <button
                                className="classes-help-trigger"
                                onClick={() => setHelpOpen(true)}
                                onMouseEnter={() => setHelpIconHovered(true)}
                                onMouseLeave={() => setHelpIconHovered(false)}
                                aria-label="Class Management Help"
                                title="How It Works">
                                <Icon
                                    path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                    size="1.62rem"
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>
                        </div>
                    </div>

                    {/* Navigation Cards Grid */}
                    <div className="classes-portal-grid">
                        {navCards.map((card) => (
                            <Link
                                key={card.path}
                                to={card.path}
                                className="content-card nav-card classes-portal-nav-card">
                                {card.step && !isTeamMember && (
                                    <span className="classes-portal-step-badge">{card.step}</span>
                                )}
                                <div className="classes-portal-card-header">
                                    <Icon path={card.icon} size={1.4} color="#451f78" />
                                    <h3 className="classes-portal-card-title">{card.title}</h3>
                                </div>
                                <p className="classes-portal-card-subtitle">{card.subtitle}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>

        <ClassesHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
        </>
    );
};

export default ManageClassesPortal;
